import { type FunctionComponent, useState } from 'react'
import styled from '@emotion/styled'
import {
  typography,
  Flex,
  SwitchToggle,
  useTheme,
} from '@retailer-platform/shared-components/src/tds'
import { notify, NotificationLevel } from '@retailer-platform/shared-components'
import { CheckboxSelectedIcon, RemoveIcon } from '@instacart/ids-core'
import { useCurrentAccountDetails } from '@retailer-platform/dashboard/utils'
import { trackEvent } from '@retailer-platform/dashboard/analytics'
import {
  NotificationSubscriptionMethodEnum,
  useSubscribeNotificationMutation,
  useUnsubscribeNotificationMutation,
} from '../../../../api'
import { ToggleType } from '../types'
import { useDomainMessage, useDomainMessages } from '../../../../utils/domain/intl'

const ColumnContent = styled.div({
  ...typography.bodyRegular,
})

interface NotificationToggleProps {
  type: ToggleType
  isSubscribed: boolean
  channelName: string
  channelSlug: string
  channelId: string
  scopeId?: string
  bannerName?: string
  onToggleSuccess: () => void
  isDisabled?: boolean
}

export const NotificationToggle: FunctionComponent<NotificationToggleProps> = ({
  type,
  isSubscribed,
  channelName,
  channelSlug,
  channelId,
  scopeId,
  bannerName,
  onToggleSuccess,
  isDisabled,
}) => {
  const theme = useTheme()
  const currentAccount = useCurrentAccountDetails()
  const [subscribe] = useSubscribeNotificationMutation()
  const [unsubscribe] = useUnsubscribeNotificationMutation()
  const [error, setError] = useState<string>()
  const i18n = useDomainMessages({
    on: 'notificationsDomain.list.label.on',
    off: 'notificationsDomain.list.label.off',
    email: 'notificationsDomain.list.columnTitle.email',
    inApp: 'notificationsDomain.list.columnTitle.inApp',
    subscribeSuccess: 'notificationsDomain.list.notification.subscribeSuccess',
    unsubscribeSuccess: 'notificationsDomain.list.notification.unsubscribeSuccess',
    subscribeError: 'notificationsDomain.list.notification.subscribeError',
    unsubscribeError: 'notificationsDomain.list.notification.unsubscribeError',
    selectBanner: 'notificationsDomain.list.placeholder.selectBanner',
    unknownBanner: 'notificationsDomain.list.unknownBanner',
    failedToLoadBannerSettings: 'notificationsDomain.list.notification.loadError',
    failedToLoadBannerSettingsFor: 'notificationsDomain.list.notification.loadError',
    noBannerSettings: 'notificationsDomain.list.notification.loadError',
  })
  const bannerSuffix = useDomainMessage('notificationsDomain.list.notification.bannerSuffix', {
    bannerName: bannerName,
  })

  const handleToggle = async () => {
    setError(undefined)
    const subscriptionMethod =
      type === ToggleType.Email
        ? NotificationSubscriptionMethodEnum.Email
        : NotificationSubscriptionMethodEnum.InApp

    const mutation = isSubscribed ? unsubscribe : subscribe
    const variables = {
      accountId: currentAccount.account.id,
      notificationChannelId: channelId,
      subscriptionMethod,
      ...(scopeId && { scopeId }),
    }
    trackEvent({
      id: `notifications.settings.toggle.${isSubscribed ? 'off' : 'on'}`,
      description: 'User toggled a notification channel',
      data: {
        channelId,
        channelName,
        channelSlug,
        subscriptionMethod,
        ...(scopeId && { scopeId }),
        ...(bannerName && { bannerName }),
      },
    })

    try {
      const result = await mutation({ variables })

      if (!result.errors) {
        notify({
          contents: `${channelName} (${type === ToggleType.Email ? i18n.email : i18n.inApp}): ${
            isSubscribed ? i18n.unsubscribeSuccess : i18n.subscribeSuccess
          }${bannerName ? ` ${bannerSuffix}` : ''}`,
          level: NotificationLevel.Success,
          icon: (
            <CheckboxSelectedIcon css={{ fill: theme.colors.systemSuccessRegular }} size={28} />
          ),
        })
        onToggleSuccess?.()
      } else {
        throw new Error(result.errors[0].message)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : i18n.subscribeError
      setError(errorMessage)
      notify({
        contents: `${channelName} (${type === ToggleType.Email ? i18n.email : i18n.inApp}): ${
          isSubscribed ? i18n.unsubscribeError : i18n.subscribeError
        }${bannerName ? ` ${bannerSuffix}` : ''}`,
        level: NotificationLevel.Error,
        icon: <RemoveIcon css={{ fill: theme.colors.systemDetrimentalRegular }} size={28} />,
        timeout: 8000,
      })
    }
  }

  return (
    <Flex.Column>
      <SwitchToggle
        isSelected={isSubscribed}
        onChange={handleToggle}
        label={isSubscribed ? i18n.on : i18n.off}
        disabled={isDisabled}
      />
      {error && (
        <ColumnContent
          css={{
            color: theme.colors.systemDetrimentalRegular,
            ...typography.bodyRegular,
            marginTop: '4px',
          }}
        >
          {error}
        </ColumnContent>
      )}
    </Flex.Column>
  )
}
